import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, ArrowRight } from "lucide-react";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Signup() {
  const { t } = useTranslation(["auth", "common"]);
  const { signUp } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    tenant_name: "",
    email: "",
    password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await signUp({
        ...form,
        email: form.email.trim(),
        name: form.name.trim(),
        tenant_name: form.tenant_name.trim(),
      });
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (err?.response?.status === 409) {
        setError(t("auth:signUp.emailTaken"));
      } else {
        setError(formatApiError(err, t("common:errors.unexpected")));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell>
      <div className="space-y-8" data-testid="signup-page">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            {t("auth:signUp.title")}
          </h1>
          <p className="mt-2 text-sm text-slate-500">{t("auth:signUp.subtitle")}</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-5" noValidate>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="su-name" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                {t("auth:signUp.nameLabel")}
              </Label>
              <Input
                id="su-name"
                data-testid="signup-name-input"
                required
                value={form.name}
                onChange={update("name")}
                placeholder={t("auth:signUp.namePlaceholder")}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="su-tenant" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                {t("auth:signUp.tenantLabel")}
              </Label>
              <Input
                id="su-tenant"
                data-testid="signup-tenant-input"
                required
                value={form.tenant_name}
                onChange={update("tenant_name")}
                placeholder={t("auth:signUp.tenantPlaceholder")}
                className="h-11"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="su-email" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {t("auth:signUp.emailLabel")}
            </Label>
            <Input
              id="su-email"
              type="email"
              autoComplete="email"
              required
              data-testid="signup-email-input"
              value={form.email}
              onChange={update("email")}
              placeholder={t("auth:signUp.emailPlaceholder")}
              className="h-11"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="su-password" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {t("auth:signUp.passwordLabel")}
            </Label>
            <Input
              id="su-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              data-testid="signup-password-input"
              value={form.password}
              onChange={update("password")}
              placeholder={t("auth:signUp.passwordPlaceholder")}
              className="h-11"
            />
          </div>

          {error && (
            <p
              data-testid="signup-error"
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={submitting}
            data-testid="signup-submit-button"
            className="h-11 w-full bg-slate-900 text-white hover:bg-slate-800"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                {t("auth:signUp.submit")}
                <ArrowRight className="ml-1 h-4 w-4" strokeWidth={2} />
              </>
            )}
          </Button>

          <p className="text-[11px] leading-relaxed text-slate-400">
            {t("auth:signUp.termsNote")}
          </p>
        </form>

        <p className="text-sm text-slate-500">
          {t("auth:signUp.haveAccount")}{" "}
          <Link
            to="/login"
            data-testid="goto-login-link"
            className="font-medium text-indigo-700 underline-offset-4 hover:underline"
          >
            {t("auth:signUp.signIn")}
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
